// THE SURFACE CATALOG — the one list that says what surfaces exist, how big
// a tile of each is in metres, and, for each, WHO OWNS ITS PIXELS.
//
// A surface is either FORGED (the client generates it at load, at
// `forgeSize`) or SHIPPED (tools/assets bakes it offline at `ship`, writes it
// to apps/web/public/assets, and the client loads it). Never both: `ship` is
// what moves a surface from one column to the other, and the day it appears
// the generator leaves runtime.ts, so the baked sheet becomes the only origin
// of that material's texels.
//
// Both halves of the pipeline read this file. tools/assets bakes exactly the
// entries with `ship`; the client forges exactly the entries without one and
// refuses to render until the rest have loaded. A drift between the two is a
// typecheck error and a failing test (see catalog.test.ts), not a mystery in
// a frame.
//
// TILE SIZES ARE POWERS OF TWO IN METRES on purpose: the floating origin
// rebases on a 128 m grid, so a world-space triplanar projection at 1/2/4 m
// shifts by a whole number of tiles and cannot pop.

export interface SurfaceSpec {
  /** The generator in surfaces.ts that makes it. */
  readonly gen: string;
  /** Its leading arguments — seed first — before the trailing `size`. */
  readonly args: readonly number[];
  /** World size of one tile, metres. Powers of two only. */
  readonly meters: number;
  /** Texels across a tile when the CLIENT forges it at load. */
  readonly forgeSize: number;
  /**
   * Texels across a tile when it is BAKED OFFLINE, which is also the flag
   * that says it is. Absent = the client still forges it.
   *
   * There is no time budget offline, so this number is set by what the
   * surface has to survive rather than by what fits in a load screen: a wall
   * the camera can stand a metre from wants ≥ 512 texels per metre, which is
   * 2048² on a 4 m tile. What it costs is committed bytes and VRAM, so it is
   * a per-surface decision and not a global default — see
   * tools/assets/README.md for the budget.
   */
  readonly ship?: number;
  /**
   * Texels across the NORMAL sheet, when neither half the albedo nor the
   * pipeline's 512² ceiling is the right answer.
   *
   * NOTHING USES IT TODAY, and that is worth recording rather than deleting.
   * The normal map is the only map whose codec costs real money — measured
   * across this catalog, a 512² UASTC normal is 180–340 kB against 97–220 kB
   * for a 1024² ETC1S albedo and 50–80 kB for a 512² ORM, and doubling one
   * quadruples it: the stucco's 1024² normal was 840 kB, more than the rest
   * of that surface put together and 10% of the whole committed payload for
   * one map of one material. Phase 3 shipped it at 512² instead, which is
   * 128 texels per metre — exactly what the runtime forge shipped and what
   * five waves of critique passed — and spent the difference on an albedo at
   * four times the forge's density, which is the map anyone can actually see.
   *
   * `reliefRef` is what makes that trade safe, and it is worth being precise
   * about what phase 1 proved: not that a normal map must be 1024², but that
   * the Sobel gain survives a change of bake resolution. It survives this one
   * too — the 2048² height field is filtered down to 512² and renormalised,
   * so the shipped normal is a supersampled average of the full-density
   * relief rather than a coarse sample of it.
   */
  readonly shipNormal?: number;
  /**
   * How the sheet is sampled. `triplanar` (the default) is projected in world
   * space by render/forge.ts and is why tile sizes must be powers of two —
   * the floating origin rebases on a 128 m grid. `uv` sheets are sampled
   * through geometry the terrain shader authored its own UVs for, at its own
   * repeat, so the rebase never touches them and the power-of-two rule does
   * not bind. The ground crust is the only `uv` family, and its repeat is
   * `DETAIL_METERS` in render/terrain.ts.
   */
  readonly projection?: "triplanar" | "uv";
}

/**
 * Every surface the world contains.
 *
 * Names are load-bearing: `dress()`, `paint()` and `surface()` take these
 * strings, and they appear across render/city.ts, render/surface.ts,
 * render/area.ts, render/poi.ts, render/scatter.ts and render/understreet.ts.
 */
// PHASE 3 SHIPS EVERY ONE OF THEM, and `FORGED_SURFACES` is now empty.
//
// That is the statement the phase exists to make. There is no code path left
// in the browser bundle that can produce a single texel of a single material
// in this game: `runtime.ts` exports an empty table, `catalog.test.ts` asserts
// that it agrees with this file, and every sheet below has exactly one origin
// — a developer's machine, once, offline, with no clock on it.
//
// THE TEXEL DENSITY COLUMN IS THE REAL DECISION HERE. `ship` is not "as big
// as we can afford"; it is what the tile has to survive, which is a function
// of how close the camera gets and how big the tile is in metres:
//
//   surface       tile   albedo    px/m    seen from
//   stucco pair    4 m   2048²      512    a hand's width — the walls of the
//                                          street the player walks down
//   wood           2 m   1024²      512    arm's length — crate lids
//   bark           2 m   1024²      512    a metre — the fen's spires
//   stone          2 m   1024²      512    a metre — boulders, cairns
//   iron/brass/    1 m    512²      512    a hand's width — the hero metal,
//     canvas                               hoops, awnings
//   paving         4 m   1024²      256    underfoot, but read by its joint
//                                          pattern rather than its grain
//   facade         4 m   1024²      256    a metre, walking the Prom
//   dirt           4 m   1024²      256    two metres, looking down
//   container      4 m   1024²      256    two metres, in the port
//   plate          4 m   1024²      256    two metres, a club's walls
//   roofTile       2 m    512²      256    six metres and up, never closer
//   sand           2 m    512²      256    drifts at a wall's foot
//   glass          2 m    512²      256    across a street
//   playa          6 m   1024²      170    five metres to the fog line
//   hull           4 m    512²      128    ten metres — the derelict barge
//
// Nothing is baked at a density its subject will never be seen at, because
// that is not free: it is committed bytes in the repository and megabytes of
// a player's VRAM. "NO TIME BUDGET" IS NOT THE SAME AS "NO BUDGET", and the
// first cut of this phase forgot it: everything the camera gets near went to
// 2048², the payload landed at 12.5 MB, and two thirds of the overrun was in
// normal maps of features too small to be normal maps at all (see the sand
// tooth note in the stucco). Only the two stucco sheets keep the hero tier,
// and NO NORMAL MAP IN THIS GAME IS LARGER THAN 512² — one rule, no
// exceptions, because the exception was a megabyte.
const CATALOG = {
  // --- Adobe stucco. The settlement kit's skin, and by area the single
  // biggest authored surface in the frontier set. Near-neutral on purpose:
  // the §c palette anchors live in the per-building tint, so this sheet owns
  // VARIATION and RELIEF and gives the hue back untouched.
  //
  // FIRST SHIPPED SURFACE. It is the right one to prove the road with: it has
  // the largest screen area in the game, it is the surface a player stands
  // closest to, and it is the only sheet whose mean luminance is PINNED
  // (`meanTarget: 0.76`), so if the offline bake moved the grade by so much
  // as a luma point the settlement would say so immediately.
  //
  // The only two sheets that keep the 2048² hero albedo. They are the walls a
  // player's face ends up against.
  // WAVE 14 — 2048 → 1024, AND THE JUDGES PAID FOR THE MEASUREMENT.
  //
  // These two were the only sheets in the catalog at 2048, and they were
  // raised there by the wave whose entire thesis was texel density. Three
  // independent verdicts then measured what that bought: "barely moving the
  // image at all", two night vistas byte-identical, and every vista's
  // luminance inside 1.3 points. A change nobody can see is not worth
  // 1.05 MB of committed payload on a product whose pitch is click-a-link
  // onboarding — and it is worth less than that here, because the stucco
  // wall this sheet dresses now has doubly-curved GEOMETRY under it
  // (render/solid.ts), which is what the same judges said the frame was
  // actually missing. The bake still runs at 2048 and box-filters down, so
  // level 0 is supersampled rather than point-sampled.
  stucco: { gen: "stucco", args: [0xada1, 1], meters: 4, forgeSize: 512, ship: 1024 },
  stuccoWorn: { gen: "stucco", args: [0xada2, 1.7], meters: 4, forgeSize: 512, ship: 1024 },

  // --- Weathered plank. Crates, barrels, handcarts, planters, boards.
  // Nails, pores and ingrained seam dirt as of phase 3.
  wood: { gen: "wood", args: [0x7d0d, 6], meters: 2, forgeSize: 512, ship: 1024 },

  // --- Hull plate: the derelict barge, and any ship skin that is not brass.
  // The lowest density in the file on purpose: nothing wearing it is ever
  // within ten metres of the lens.
  hull: { gen: "hullPlate", args: [0x4c11], meters: 4, forgeSize: 512, ship: 512 },

  // --- Bark: the fen's dead spires. Grain runs along V, because V is world
  // UP on every side plane of the projection and bark does not run in rings.
  bark: { gen: "bark", args: [0xba46], meters: 2, forgeSize: 256, ship: 1024 },

  // --- Wrought iron: hoops, wheels, brackets, poles, vents.
  iron: { gen: "iron", args: [0x1207], meters: 1, forgeSize: 256, ship: 512 },

  // --- Brass: the condenser dome, trim rings, the hero-metal beat.
  brass: { gen: "brass", args: [0xb7a5], meters: 1, forgeSize: 256, ship: 512 },

  // --- Canvas awning, weathered and striped.
  canvas: { gen: "canvasCloth", args: [0xca70], meters: 1, forgeSize: 256, ship: 512 },

  // --- Stone: boulders, rubble, masonry drums, cairns.
  stone: { gen: "stone", args: [0x570e], meters: 2, forgeSize: 256, ship: 1024 },

  // --- Paving slabs for the pads that are still paved. The floor the camera
  // stands on, so it buys the hero albedo density — but at a 512² normal,
  // because a pavement is read by its joint pattern and its polish, not by
  // its micro-relief.
  paving: { gen: "paving", args: [0x9afe], meters: 4, forgeSize: 256, ship: 1024 },

  // --- Roof tile: pantile courses for the settlement roofline.
  roofTile: { gen: "roofTile", args: [0x700f], meters: 2, forgeSize: 256, ship: 512 },

  // --- Packed dirt / track: the service bay, the spaceport apron.
  dirt: { gen: "dirt", args: [0xd127], meters: 4, forgeSize: 256, ship: 1024 },

  // --- Wind-rippled sand: drifts banked against the wall feet.
  sand: { gen: "sand", args: [0x5a4d], meters: 2, forgeSize: 256, ship: 512 },

  // --- Dirty glass: the kit's windows. The biggest single jump in the file
  // — 128² forged to 512² baked — because it was the sheet the runtime
  // budget hurt most: a window had to be cheap, so it was 128 texels, so it
  // could not hold a rain track or a drawn-glass ripple.
  glass: { gen: "glass", args: [0x91a5], meters: 2, forgeSize: 128, ship: 512 },

  // --- Corrugated container steel: the port's stacked freight. NEUTRAL —
  // see the generator; a container yard's colour is per-box, not per-sheet.
  container: { gen: "containerSteel", args: [0xc0a7], meters: 4, forgeSize: 512, ship: 1024 },

  // --- Bolted industrial plate: gantries, blast walls, bowsers, kerb
  // furniture, kiosks, club walls. Neutral for the same reason.
  plate: { gen: "steelPlate", args: [0x91ad], meters: 4, forgeSize: 512, ship: 1024 },

  // --- Meridian's facade kit: the sheet two thousand instanced towers and
  // every Prom building wear. It lived in render/city.ts and was forged at
  // 512² alongside everything else the city makes; it is the one surface in
  // the game whose subject the camera walks THROUGH, and at 128 texels per
  // metre its 6 cm mullions were eight texels wide.
  facade: { gen: "cityFacade", args: [0xfa11], meters: 4, forgeSize: 512, ship: 1024 },

  // --- THE GROUND CRUST. Sampled through the terrain shader's own UV set at
  // DETAIL_METERS, not by the world-space triplanar projection — hence
  // `projection: "uv"`, and hence a tile size that is not a power of two.
  //
  // The playa is the largest single surface in the game by a wide margin: on
  // the frontier vistas it is most of the lower half of every frame. It was
  // drawn on a 2D canvas in render/textures.ts at 512² over a 6 m repeat —
  // 85 texels per metre, the coarsest sheet in the world on the surface with
  // the most screen area, because it was also the most expensive thing the
  // load screen was making.
  playa: {
    gen: "playaCrust",
    args: [0x9a17],
    meters: 6,
    forgeSize: 512,
    ship: 1024,
    projection: "uv",
  },
} as const satisfies Record<string, SurfaceSpec>;

export type SurfaceName = keyof typeof CATALOG;

/**
 * Widened to `SurfaceSpec` on the way out. The literal object is `as const`
 * so the NAMES are inferred and a typo is a typecheck error; the exported
 * view is uniform so `spec.ship` is a question you can ask of any entry
 * rather than one that only compiles for the entries that answer yes.
 */
export const SURFACE_CATALOG: Readonly<Record<SurfaceName, SurfaceSpec>> = CATALOG;

/** The surfaces tools/assets bakes and the client loads. */
export const SHIPPED_SURFACES: readonly SurfaceName[] = (
  Object.keys(SURFACE_CATALOG) as SurfaceName[]
).filter((name) => SURFACE_CATALOG[name].ship !== undefined);

/** The surfaces the client still generates at load. */
export const FORGED_SURFACES: readonly SurfaceName[] = (
  Object.keys(SURFACE_CATALOG) as SurfaceName[]
).filter((name) => SURFACE_CATALOG[name].ship === undefined);
